import React, { useEffect, useState } from 'react';
import { Box, Divider, Modal, MenuItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useLocation } from 'react-router-dom';
import { getAccountSettingsItemLabel, isAccountNavItemActive } from './accountSettingsNav.utils';

interface MenuItemType {
    name: string;
    route: string;
    label?: string;
}

interface AccountSettingsMenuProps {
    menuItems: MenuItemType[];
    onItemClick: (menuItem: MenuItemType) => void;
}

/** Icon mapping for account settings nav items (sidebar + mobile). */
export const getIconForMenuItem = (itemName: string) => {
    const name = itemName.toLowerCase();
    if (name === 'settings' || name.includes('profile') || name.includes('account')) return <SettingsIcon />;
    if (name.includes('security') || name.includes('password')) return <SecurityIcon />;
    if (name.includes('notification')) return <NotificationsIcon />;
    if (name.includes('payment') || name.includes('billing')) return <PaymentIcon />;
    if (name.includes('subscription')) return <SubscriptionsIcon />;
    if (name.includes('communication')) return <EmailOutlinedIcon />;
    if (name.includes('preference')) return <TuneIcon />;
    if (name.includes('bank') || name.includes('external')) return <AccountBalanceIcon />;
    return <SettingsIcon />;
};

export const AccountSettingsMenu: React.FC<AccountSettingsMenuProps> = ({ menuItems, onItemClick }) => {
    const [selectedItem, setSelectedItem] = useState<string>(menuItems[0].name);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const muiTheme = useTheme();
    const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
    const { themeStyle } = useThemeContext();
    const location = useLocation();

    useEffect(() => {
        const match = menuItems.find(item => isAccountNavItemActive(location.pathname, item.route));
        if (match) {
            setSelectedItem(match.name);
        }
    }, [location.pathname, menuItems]);

    const handleMenuItemClick = (item: MenuItemType) => {
        setSelectedItem(item.name);
        setOpenModal(false);
        onItemClick && onItemClick(item);
    };

    const handleMobileOpen = () => setOpenModal(true);
    const handleMobileClose = () => setOpenModal(false);

    return (
        <Box sx={{ width: '100%' }}>
            {!isMdUp ? (
                // Mobile: Dropdown style
                <Box>
                    <Stack
                        direction="row"
                        width={'100%'}
                        justifyContent="space-between"
                        p={2}
                        border={'1px solid rgba(255,255,255,0.08)'}
                        bgcolor={'rgba(24,24,24,0.72)'}
                        alignItems="center"
                        mb={2}
                        borderRadius={2}
                        onClick={handleMobileOpen}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {getIconForMenuItem(selectedItem)}
                            <Typography
                                style={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    textTransform: 'none',
                                }}>
                                {getAccountSettingsItemLabel(
                                    menuItems.find(i => i.name === selectedItem) ?? { name: selectedItem },
                                )}
                            </Typography>
                        </Stack>
                        <ChevronDownIcon sx={{ color: theme[themeStyle].highlight }} />
                    </Stack>

                    {/* Modal for Mobile Menu */}
                    <Modal open={openModal} onClose={handleMobileClose}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '90%',
                                bgcolor: '#1a1a1a',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 2,
                                boxShadow: 24,
                                p: 2,
                            }}>
                            {menuItems.map((item, index) => {
                                return (
                                    <Box key={index}>
                                        <MenuItem
                                            onClick={() => handleMenuItemClick(item)}
                                            selected={isAccountNavItemActive(location.pathname, item.route)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 14,
                                                textTransform: 'none',
                                                gap: 1,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(40,40,40,0.52)',
                                                    color: theme[themeStyle].primary_orange,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(40,40,40,0.72)',
                                                    color: theme[themeStyle].highlight,
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: 'rgba(40,40,40,0.85)',
                                                },
                                            }}>
                                            {getIconForMenuItem(item.name)}
                                            {getAccountSettingsItemLabel(item)}
                                        </MenuItem>
                                        {index < menuItems.length - 1 && (
                                            <Divider sx={{ backgroundColor: theme[themeStyle].darkGray60 }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Modal>
                </Box>
            ) : null}
        </Box>
    );
};
